import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Snackbar,
  Alert,
  CircularProgress,
  Chip,
  Tooltip,
  Grid
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  LocalOffer as PackageIcon
} from '@mui/icons-material';
import { API_BASE_URL } from '../../config';

// 管理员套餐API基础路径
const ADMIN_API_BASE = `${API_BASE_URL}/kOjtx3AXNmCsT5Wz`;

const PackageManagement = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    amount: '',
    price: '',
    originalPrice: '',
    description: '',
    sort: 0,
    isActive: true
  });
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const showMessage = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // 获取套餐列表
  const fetchPackages = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${ADMIN_API_BASE}/recharge/packages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setPackages(data.data);
      } else {
        showMessage('获取套餐列表失败', 'error');
      }
    } catch (error) {
      console.error('获取套餐失败:', error);
      showMessage('获取套餐列表失败', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  // 打开添加对话框
  const handleOpenAdd = () => {
    setEditingPackage(null);
    setFormData({
      name: '',
      amount: '',
      price: '',
      originalPrice: '',
      description: '',
      sort: 0,
      isActive: true
    });
    setDialogOpen(true);
  };

  // 打开编辑对话框
  const handleOpenEdit = (pkg) => {
    setEditingPackage(pkg);
    setFormData({
      name: pkg.name,
      amount: pkg.amount.toString(),
      price: (pkg.priceYuan || pkg.price / 100).toString(),
      originalPrice: (pkg.originalPriceYuan || (pkg.originalPrice || pkg.price) / 100).toString(),
      description: pkg.description || '',
      sort: pkg.sort || 0,
      isActive: pkg.isActive
    });
    setDialogOpen(true);
  };

  // 关闭对话框
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingPackage(null);
  };

  // 保存套餐
  const handleSave = async () => {
    if (!formData.name || !formData.amount || !formData.price) {
      showMessage('请填写完整信息', 'warning');
      return;
    }

    const amount = parseInt(formData.amount);
    const price = parseFloat(formData.price);

    if (amount < 1) {
      showMessage('面值不能小于1', 'warning');
      return;
    }

    if (price < 0.01) {
      showMessage('价格不能小于0.01元', 'warning');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('authToken');
      const url = `${ADMIN_API_BASE}/recharge/packages`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: editingPackage?.id || editingPackage?._id,
          name: formData.name,
          amount: amount,
          price: price,
          originalPrice: formData.originalPrice ? parseFloat(formData.originalPrice) : price,
          description: formData.description,
          sort: parseInt(formData.sort) || 0,
          isActive: formData.isActive
        })
      });

      const data = await response.json();

      if (data.success) {
        showMessage(editingPackage ? '套餐已更新' : '套餐已创建', 'success');
        handleCloseDialog();
        fetchPackages();
      } else {
        showMessage(data.message || '保存失败', 'error');
      }
    } catch (error) {
      console.error('保存套餐失败:', error);
      showMessage('保存失败', 'error');
    } finally {
      setSaving(false);
    }
  };

  // 删除套餐
  const handleDelete = async (pkg) => {
    if (!window.confirm(`确定要删除套餐"${pkg.name}"吗？`)) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      // 使用 pkg.id（后端返回的字段），而不是 pkg._id
      const packageId = pkg.id || pkg._id;
      const response = await fetch(`${ADMIN_API_BASE}/recharge/packages/${packageId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();

      if (data.success) {
        showMessage('套餐已删除', 'success');
        fetchPackages();
      } else {
        showMessage(data.message || '删除失败', 'error');
      }
    } catch (error) {
      console.error('删除套餐失败:', error);
      showMessage('删除失败', 'error');
    }
  };

  // 格式化金额
  const formatPrice = (fen) => {
    return `¥${(fen / 100).toFixed(2)}`;
  };

  // 格式化面值
  const formatAmount = (amount) => {
    if (amount >= 10000) {
      return `${(amount / 10000).toFixed(0)}万字`;
    }
    return `${amount}字`;
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Paper sx={{ p: 3 }}>
        {/* 标题栏 */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" alignItems="center" gap={2}>
            <PackageIcon sx={{ color: '#00ff9f', fontSize: 32 }} />
            <Typography variant="h5" sx={{ color: '#00ff9f', fontWeight: 'bold' }}>
              充值套餐管理
            </Typography>
          </Box>
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchPackages}
              sx={{ borderColor: '#00ff9f', color: '#00ff9f' }}
            >
              刷新
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenAdd}
              sx={{ bgcolor: '#00ff9f', color: '#000', '&:hover': { bgcolor: '#00cc7f' } }}
            >
              添加套餐
            </Button>
          </Box>
        </Box>

        {/* 提示信息 */}
        <Box mb={3} p={2} sx={{ bgcolor: 'rgba(0, 255, 159, 0.1)', borderRadius: 2 }}>
          <Typography variant="body2" sx={{ color: '#00ff9f' }}>
            <strong>提示：</strong>
            只有通过系统初始化创建的"体验卡"（3万字）标记为限购，每个账号仅可购买一次。
            其他所有套餐均可重复购买。管理员手动添加的套餐不受限购限制。
          </Typography>
        </Box>

        {/* 套餐列表 */}
        {loading ? (
          <Box textAlign="center" py={4}>
            <CircularProgress sx={{ color: '#00ff9f' }} />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'rgba(0, 30, 60, 0.5)' }}>
                  <TableCell sx={{ color: '#00ff9f', fontWeight: 'bold' }}>排序</TableCell>
                  <TableCell sx={{ color: '#00ff9f', fontWeight: 'bold' }}>套餐名称</TableCell>
                  <TableCell sx={{ color: '#00ff9f', fontWeight: 'bold' }}>面值（字数）</TableCell>
                  <TableCell sx={{ color: '#00ff9f', fontWeight: 'bold' }}>售价</TableCell>
                  <TableCell sx={{ color: '#00ff9f', fontWeight: 'bold' }}>原价</TableCell>
                  <TableCell sx={{ color: '#00ff9f', fontWeight: 'bold' }}>折扣</TableCell>
                  <TableCell sx={{ color: '#00ff9f', fontWeight: 'bold' }}>限购</TableCell>
                  <TableCell sx={{ color: '#00ff9f', fontWeight: 'bold' }}>状态</TableCell>
                  <TableCell sx={{ color: '#00ff9f', fontWeight: 'bold' }}>描述</TableCell>
                  <TableCell sx={{ color: '#00ff9f', fontWeight: 'bold' }} align="center">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {packages.map((pkg) => (
                  <TableRow key={pkg._id} hover>
                    <TableCell>{pkg.sort || 0}</TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 'bold' }}>{pkg.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={formatAmount(pkg.amount)}
                        size="small"
                        sx={{ bgcolor: 'rgba(0, 255, 159, 0.2)', color: '#00ff9f' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ color: '#00ff9f', fontWeight: 'bold' }}>
                        {formatPrice(pkg.price)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ color: 'text.secondary', textDecoration: pkg.discount ? 'line-through' : 'none' }}>
                        {formatPrice(pkg.originalPrice || pkg.price)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {pkg.discount ? (
                        <Chip label={`${pkg.discount}折`} size="small" color="error" />
                      ) : (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {pkg.isExperience ? (
                        <Chip label="限购" size="small" sx={{ bgcolor: 'rgba(251, 191, 36, 0.2)', color: '#fbbf24', border: '1px solid rgba(251, 191, 36, 0.4)', fontWeight: 700 }} />
                      ) : (
                        <Typography variant="body2" sx={{ color: 'rgba(224, 242, 254, 0.35)' }}>不限制</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={pkg.isActive ? '启用' : '禁用'}
                        size="small"
                        color={pkg.isActive ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 200 }}>
                        {pkg.description || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="编辑">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenEdit(pkg)}
                          sx={{ color: '#00ff9f' }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="删除">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(pkg)}
                          sx={{ color: 'error.main' }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {packages.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      <Typography sx={{ color: 'text.secondary' }}>
                        暂无套餐，点击"添加套餐"创建
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* 添加/编辑对话框 */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: '#1a1a2e' } }}
      >
        <DialogTitle sx={{ color: '#00ff9f' }}>
          {editingPackage ? '编辑套餐' : '添加套餐'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="套餐名称"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              placeholder="如：体验套餐、基础套餐"
              InputProps={{ sx: { color: '#fff' } }}
              InputLabelProps={{ sx: { color: '#00ff9f' } }}
            />

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="面值（字数）"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  fullWidth
                  type="number"
                  placeholder="30000"
                  helperText="用户将获得的字数"
                  InputProps={{ sx: { color: '#fff' } }}
                  InputLabelProps={{ sx: { color: '#00ff9f' } }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="售价（元）"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  fullWidth
                  type="number"
                  placeholder="10.00"
                  helperText="用户支付金额"
                  InputProps={{ sx: { color: '#fff' } }}
                  InputLabelProps={{ sx: { color: '#00ff9f' } }}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="原价（元）"
                  value={formData.originalPrice}
                  onChange={(e) => setFormData({ ...formData, originalPrice: e.target.value })}
                  fullWidth
                  type="number"
                  placeholder="15.00"
                  helperText="用于显示折扣"
                  InputProps={{ sx: { color: '#fff' } }}
                  InputLabelProps={{ sx: { color: '#00ff9f' } }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="排序"
                  value={formData.sort}
                  onChange={(e) => setFormData({ ...formData, sort: e.target.value })}
                  fullWidth
                  type="number"
                  placeholder="0"
                  helperText="数字越小越靠前"
                  InputProps={{ sx: { color: '#fff' } }}
                  InputLabelProps={{ sx: { color: '#00ff9f' } }}
                />
              </Grid>
            </Grid>

            <TextField
              label="描述"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
              placeholder="套餐描述，用于前端显示"
              InputProps={{ sx: { color: '#fff' } }}
              InputLabelProps={{ sx: { color: '#00ff9f' } }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#00ff9f' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#00ff9f' }
                  }}
                />
              }
              label="启用此套餐"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDialog} sx={{ color: 'text.secondary' }}>
            取消
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{ bgcolor: '#00ff9f', color: '#000', '&:hover': { bgcolor: '#00cc7f' } }}
          >
            {saving ? <CircularProgress size={20} /> : '保存'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 消息提示 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PackageManagement;
